<template>
  <form
      @submit.prevent="validate"
      v-if="booking.period"
  >
    <div>
      <div class="alert alert-warning" v-if="allLockersSoldOut && event.has_electronic_lockers">
        {{ $t('soldOutElectronic') }}
      </div>

      <div class="alert alert-warning" v-else-if="allLockersSoldOut && booking.period.lockers_sold_out_message">
        {{ booking.period.lockers_sold_out_message }}
      </div>

      <ul class="list-group">
        <li
            v-for="(line, index) in booking.lines"
            v-bind:key="index"
            class="locker-item list-group-item"
            @click="selectLockerSize(line)"
        >
            <p class="mb-0 text-start">
              <strong>{{ line.name }}</strong>
            </p>
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-2 gap-sm-3 flex-fill">
            <div class="d-flex align-items-center justify-content-between flex-wrap flex-fill text-start">
              <p class="w-100 mb-0 text-muted text-start">
                <small>
                  {{ line.description }}
                </small>
              </p>

              <p
                  v-if="event.show_locker_size_details"
                  class="w-100 mb-0 text-muted text-start"
              >
                <small>
                  {{ $t('lockerSizes.'+ line.locker_size_id) }}
                </small>
              </p>
            </div>
            <p class="mb-0 text-end ms-auto ps-2">
                            <span v-if="line.available">
                                {{ $n(line.price, 'currency') }}
                            </span>

              <em v-else>
                {{ $t('soldOut') }}
              </em>
            </p>
          </div>
            <div
              v-if="line.available"
              class="locker-quantity-controls d-flex align-items-center justify-content-end gap-1 ms-2 ms-sm-3"
              :class="{ 'hover-plus': line.quantity === 0 }"
          >
            <transition
                name="locker-quantity-controls"
                mode="out-in"
            >
                  <span class="d-flex align-items-center gap-1" :class="{ 'visible pe-auto': showControls(line), 'invisible pe-none': !showControls(line) }">
                      <span
                          class="dot"
                          @click.stop="decreaseQuantity(line)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Pro v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M320 96C443.7 96 544 196.3 544 320C544 443.7 443.7 544 320 544C196.3 544 96 443.7 96 320C96 196.3 196.3 96 320 96zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM224 304C215.2 304 208 311.2 208 320C208 328.8 215.2 336 224 336L416 336C424.8 336 432 328.8 432 320C432 311.2 424.8 304 416 304L224 304z"/></svg>
                      </span>

                      <span v-if="inPosMode()"
                            class="line-quantity">
                          {{ line.quantity }}
                      </span>
                      <input v-else
                             v-model="line.quantity"
                             type="number"
                             min="0"
                             :max="maxQuantity"
                             class="form-control form-control-sm text-center"
                             @click.stop
                      >
                  </span>
            </transition>

            <span
                class="dot plus"
                @click.stop="increaseQuantity(line)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Pro v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M320 96C443.7 96 544 196.3 544 320C544 443.7 443.7 544 320 544C196.3 544 96 443.7 96 320C96 196.3 196.3 96 320 96zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM304 416C304 424.8 311.2 432 320 432C328.8 432 336 424.8 336 416L336 336L416 336C424.8 336 432 328.8 432 320C432 311.2 424.8 304 416 304L336 304L336 224C336 215.2 328.8 208 320 208C311.2 208 304 215.2 304 224L304 304L224 304C215.2 304 208 311.2 208 320C208 328.8 215.2 336 224 336L304 336L304 416z"/></svg>
            </span>
          </div>
          </div>
        </li>
      </ul>

      <div class="text-end pt-2"
           v-if="!event.fees_including && booking.getTotalFees(event) > 0"
      >
        <small>{{ $t('serviceFee', [$n(booking.getTotalFees(event), 'currency')]) }}</small>
      </div>

      <booking-summary
          :event="event"
          :booking="booking"
          class="mt-3 mb-0"
      ></booking-summary>

      <div class="d-flex justify-content-between align-items-center gap-4 pt-3" v-if="canGoBack || totalLockers > 0">
        <button
            v-if="canGoBack"
            type="button"
            class="btn btn-link btn-sm ms-n2"
            :class="{ 'disabled': saving }"
            :disabled="saving"
            @click="redirect"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
               class="bi bi-arrow-left-short" viewBox="0 0 16 16">
            <path fill-rule="evenodd"
                  d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"/>
          </svg>
          <span class="ms-1">{{ $t('back') }}</span>
        </button>

        <template v-if="totalLockers > 0">
          <template v-if="isCashier()">
            <template v-if="!saving">
              <button
                  v-if="event.cash"
                  type="submit"
                  class="btn btn-success flex-fill"
                  :class="{ 'disabled': saving }"
                  @click="save('cash')"
                  :disabled="saving"
              >
                {{ $t('cash') }}
              </button>

              <button
                  type="submit"
                  class="btn btn-success flex-fill"
                  :class="{ 'disabled': saving }"
                  @click="save()"
                  :disabled="saving"
              >
                {{ $t('PIN') }}
              </button>

              <button
                  v-if="event.use_reservations"
                  type="submit"
                  class="btn btn-success flex-fill"
                  :class="{ 'disabled': saving }"
                  @click="save('reservation')"
                  :disabled="saving"
              >
                {{ $t('reservation') }}
              </button>

              <button
                  type="submit"
                  class="btn btn-success flex-fill"
                  :class="{ 'disabled': saving }"
                  @click="save('service')"
                  :disabled="saving"
              >
                {{ $t('service') }}
              </button>
            </template>

            <button
                v-else
                type="button"
                class="btn btn-success flex-fill"
                :class="{ 'disabled': saving }"
                :disabled="saving"
            >
              <template>
                <span class="spinner-border" role="status" aria-hidden="true"></span>
                {{ $t('loading') }}
              </template>
            </button>
          </template>

          <template v-else-if="inPosMode()">
            <button
                v-if="inPosMode()"
                type="submit"
                class="btn btn-success bg-primary"
                :disabled="saving"
            >
              <template v-if="!saving">
                {{ $t('pay') }}
              </template>

              <template v-else>
                <span class="spinner-border" role="status" aria-hidden="true"></span>
                {{ $t('loading') }}
              </template>
            </button>
          </template>

          <button
              v-else
              type="submit"
              class="btn btn-success flex-fill"
          >
            {{ $t('continue') }}
          </button>
        </template>
      </div>
    </div>
  </form>
</template>

<script>
import BookingSummary from '../components/BookingSummary';
import eventBus from '../eventBus';
import posMixin from '../mixins/posMixin';
import moment from 'moment';

const maxQuantity = 10; // Default max quantity of lockers
const redirectTimeout = 45; // Redirect timeout in seconds

export default {
  components: {
    BookingSummary
  },

  mixins: [posMixin],

  props: [
    'event',
    'booking',
    'saving'
  ],

  data() {
    return {
      redirectTimer: null
    }
  },

  computed: {
    /**
     * Can we go back?
     * - To periods
     * - Or to zones
     */
    canGoBack() {
      return this.multiplePeriods || this.booking.can_select_zone;
    },

    /**
     * Visible periods. (Filter periods )
     * @returns {*}
     */
    visiblePeriods() {
      if (this.inPosMode()) {
        // Check active periods
        return this.event.periods.filter(period => {
          return moment(period.assign_at).isBefore() && moment(period.end).isAfter();
        });
      }

      // All periods
      return this.event.periods;
    },

    /**
     * Does this event have multiple periods?
     */
    multiplePeriods() {
      return this.visiblePeriods.length > 1;
    },

    /**
     * Total number of lockers.
     */
    totalLockers() {
      return this.booking.totalLockers;
    },

    /**
     * Have all lockers been sold out?
     * @returns {boolean | void}
     */
    allLockersSoldOut() {
      return this.booking.lines.every((line) => {
        return !line.available;
      });
    },

    /**
     * Maximum amount of lockers that can be booked.
     *  - If lockers are free, only 1 locker can be booked.
     *  - Otherwise use the default maximum amount of lockers
     * @returns {number}
     */
    maxQuantity() {
      if (this.inPosMode()) {
        return maxQuantity;
      }

      return this.booking.isFree() ? 1 : maxQuantity;
    }
  },

  watch: {
    '$route': 'init',
    booking: {
      handler() {
        this.checkIdle();
      },
      deep: true
    }
  },

  created() {
    this.init();
  },

  beforeDestroy() {
    this.stopRedirectTimer();
  },

  methods: {
    /**
     * Initialise component, check for period and idle.
     */
    init() {
      if (!this.booking.period) {
        this.$router.push('/event/' + this.event.code);
      }

      this.checkIdle();
    },

    /**
     * Validate this form, if valid emit event 'ready' and go to next page.
     */
    validate() {
      this.stopRedirectTimer();

      eventBus.$emit('clearErrors');

      if (+this.totalLockers === 0) {
        eventBus.$emit('error', this.$t('errors.noLockers'));
        return;
      }

      this.$emit('ready', this.booking, this.isKiosk());

      if (!this.inPosMode()) {
        this.$router.push('/event/' + this.event.code + '/details');
      }
    },

    /**
     * Show controls.
     * - Line has quantity.
     * - Line has no quantity set.
     *
     * @param {Object} line
     * @return Boolean
     */
    showControls(line) {
      return line.quantity > 0 || line.quantity === '';
    },

    /**
     * Increase quantity of line with one.
     *
     * @param {Object} line
     */
    increaseQuantity(line) {
      if (line.quantity === this.maxQuantity) {
        return;
      }

      // Only 1 locker can be booked for free.
      if (this.booking.isFree() && this.booking.totalQuantity() === this.maxQuantity) {
        return;
      }

      line.quantity++;
    },

    /**
     * Decrease quantity of line with one.
     *
     * @param {Object} line
     */
    decreaseQuantity(line) {
      if (line.quantity === 0) {
        return;
      }

      line.quantity--;
    },

    /**
     * Select locker size.
     *
     * @param {Object} line
     */
    selectLockerSize(line) {
      if (!line.available) {
        return;
      }

      if (+line.quantity > 0) {
        return;
      }

      this.increaseQuantity(line);
    },

    /**
     * Check if we need to start the idle redirect.
     */
    checkIdle() {
      if (!this.inPosMode() || !this.multiplePeriods) {
        return;
      }

      this.startRedirectTimer();
    },

    /**
     * Start timer for redirecting.
     */
    startRedirectTimer() {
      if (this.redirectTimer !== null) {
        clearTimeout(this.redirectTimer);
      }

      this.redirectTimer = setTimeout(this.redirect, redirectTimeout * 1000);
    },

    /**
     * Stop timer for redirecting.
     */
    stopRedirectTimer() {
      if (this.redirectTimer === null) {
        return;
      }

      clearTimeout(this.redirectTimer);

      this.redirectTimer = null;
    },

    /**
     * Redirect back to booking.
     */
    redirect() {
      if (this.booking.can_select_zone && !this.multiplePeriods) {
        // Back to zone selection
        this.booking.preferred_zone = null;
      }

      this.booking.reset();

      this.$router.push({
        name: 'booking',
        params: {
          code: this.event.code,
          requestedEvent: this.event
        }
      });
    },

    /**
     * @param {String?} provider
     *
     * Save booking.
     */
    save(provider) {
      if (this.saving) {
        return;
      }

      const booking = this.booking;
      delete booking.payment_provider;

      if (provider === 'cash' || provider === 'reservation' || provider === 'service') {
        booking.payment_provider = provider;
      }

      this.$emit('ready', booking, true);
    }
  }
}
</script>

<style lang="scss">
.list-group-item {
  > div {
    min-height: 30px;
  }
}
</style>
